import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SignIn } from '@phosphor-icons/react';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage:
            'url(https://static.prod-images.emergentagent.com/jobs/6cece927-fe7d-443b-b094-47167d0b0812/images/7d143e703940efae1c05ef82964aca5a26bc8cd9e9d52ca241a856d900ceb3f5.png)',
        }}
      >
        <div className="absolute inset-0 bg-[#1D9E75]/10"></div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/files/logo-v8-black.png`}
              alt="Dextra"
              className="h-20 mb-4 object-contain"
              style={{ maxWidth: '260px' }}
              data-testid="login-logo"
            />
            <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Welcome Back
            </h1>
            <p className="text-[#52525B] text-sm">Sign in to access your payroll dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@payroll.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="h-11"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-[#1D9E75] hover:bg-[#0F6E56] text-white font-semibold rounded-sm"
            >
              {loading ? (
                'Signing in...'
              ) : (
                <>
                  <SignIn size={20} className="mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>
          <div className="text-center mt-4 text-sm text-gray-600">
            New to Office Prime?{' '}
            <a href="/signup" className="font-semibold text-[#1D9E75] hover:underline" data-testid="login-signup-link">
              Create a workspace →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
